import { Fragment, useState, useEffect } from 'react';

import Grid from '@mui/material/Grid2';
import { Card, Stack, Box, Typography, Button, TextField } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { TextEditedContent } from 'components';
import PropTypes from 'prop-types';
import api from 'utils/api';

import { DOLPHIN_API } from 'shared/constants/apis';
import { AdminModal } from '../components/styles';
import { useSnackbar } from 'notistack';

const propTypes = {
  close: PropTypes.func,
  fetchContracts: PropTypes.func,
};

const IWGDashboardContractsNew = ({ close, fetchContracts }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [formState, setFormState] = useState({
    srcOrganizationCode: '',
    srcOrgId: -1,
    dstOrganizationCode: '',
    dstOrgId: -1,
    quota: 500,
    used: 0,
    monthlyInvoiceDate: 25,
  });
  const [incompleteFields, setIncompleteFields] = useState([]);
  const [incompleteWarningModalOpen, setIncompleteWarningModalOpen] =
    useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [options, setOptions] = useState({
    srcOrganizationOptions: [],
    dstOrganizationOptions: [],
  });

  useEffect(() => {
    const getOptions = async () => {
      const organizations = await api.get(DOLPHIN_API.ADMIN_ORGS, {});
      const organizationOptions = organizations.map((org) => {
        return {
          label: org.organizationCode + ' (' + org.name + ')',
          value: {
            organizationId: org.id,
            organizationCode: org.organizationCode,
          },
        };
      });
      setOptions({
        srcOrganizationOptions: organizationOptions,
        dstOrganizationOptions: organizationOptions,
      });
    };
    getOptions();
  }, []);

  const keyToCheck = ['srcOrganizationCode', 'dstOrganizationCode', 'quota'];

  const keyToLabel = {
    srcOrganizationCode: 'Source Organization Code',
    dstOrganizationCode: 'Destination Organization Code',
    quota: 'Quota',
    used: 'Used',
  };

  const onValueChange = (key, value) => {
    setFormState((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };

  const sendToServer = async () => {
    const data = { ...formState };
    await api.post(DOLPHIN_API.ADMIN_CONTRACTS, {
      data: {
        createdData: [data],
      },
    });
    await fetchContracts();
    enqueueSnackbar('Contract created successfully', {
      variant: 'success',
    });
    close();
  };

  const checkIfFormCompleted = () => {
    const _incompleteFields = [];

    keyToCheck.forEach((key) => {
      const value = formState[key];
      if (value.length === 0) {
        _incompleteFields.push(keyToLabel[key]);
      }
    });

    if (_incompleteFields.length > 0) {
      setIncompleteFields(_incompleteFields);
      return false;
    } else {
      return true;
    }
  };

  const handleSubmitClick = async (event) => {
    event.preventDefault();
    if (!checkIfFormCompleted()) {
      setIncompleteWarningModalOpen(true);
    } else {
      setConfirmModalOpen(true);
    }
  };

  return (
    <Fragment>
      <Box py={3} px={3} mt={3} mx={3}>
        <Stack spacing={3}>
          <Autocomplete
            disablePortal
            id="organizationCode"
            onChange={(event, newValue) => {
              if (newValue && newValue.value) {
                onValueChange(
                  'srcOrganizationCode',
                  newValue.value.organizationCode
                );
                onValueChange(
                  'srcOrganizationId',
                  newValue.value.organizationId
                );
              }
            }}
            options={options.srcOrganizationOptions}
            renderInput={(params) => {
              return (
                <TextField
                  {...params}
                  name="srcOrganizationCode"
                  fullWidth
                  variant="outlined"
                  label="Source Organization Code"
                  color="secondary"
                />
              );
            }}
          />

          <Autocomplete
            disablePortal
            id="organizationCode"
            onChange={(event, newValue) => {
              if (newValue && newValue.value) {
                onValueChange(
                  'dstOrganizationCode',
                  newValue.value.organizationCode
                );
                onValueChange(
                  'dstOrganizationId',
                  newValue.value.organizationId
                );
              }
            }}
            options={options.dstOrganizationOptions}
            renderInput={(params) => {
              return (
                <TextField
                  {...params}
                  name="dstOrganizationCode"
                  fullWidth
                  variant="outlined"
                  label="Destination Organization Code"
                  color="secondary"
                />
              );
            }}
          />

          <TextField
            id="quota"
            name="quota"
            fullWidth
            variant="outlined"
            label="Quota"
            onChange={(event) =>
              onValueChange('quota', parseInt(event.target.value) || 0)
            }
            value={formState.quota}
            color="secondary"
          />

          <Grid
            spacing={3}
            container
            alignItems="flex-start"
            justifyContent="space-between">
            <Grid>
              <Button
                variant="contained"
                color="primary"
                style={{
                  color: 'white',
                }}
                onClick={handleSubmitClick}>
                Submit
              </Button>
            </Grid>
            <Grid>
              <Button variant="contained" color="secondary" onClick={close}>
                Cancel
              </Button>
            </Grid>
          </Grid>
        </Stack>

        {/* Modal for warning */}
        <AdminModal
          open={incompleteWarningModalOpen}
          onClose={() => setIncompleteWarningModalOpen(false)}>
          <Card sx={{ margin: 4, marginTop: 3, width: 800 }}>
            <Stack spacing={3} py={3} px={3} mt={3} mx={3}>
              <Typography mb={1}>Warning</Typography>
              <Typography>The following information is incomplete!</Typography>
              <br />
              {incompleteFields &&
                incompleteFields.map((field, index) => (
                  <p style={{ color: 'red' }} key={index}>
                    {field}
                  </p>
                ))}
              <br />
              <Button
                variant="contained"
                color="secondary"
                onClick={() => setIncompleteWarningModalOpen(false)}>
                Close
              </Button>
            </Stack>
          </Card>
        </AdminModal>
        {/* Modal for confirm */}
        <AdminModal
          open={confirmModalOpen}
          onClose={() => setConfirmModalOpen(false)}>
          <Card sx={{ margin: 4, marginTop: 3, width: 800 }}>
            <Box py={3} px={3} mt={3} mx={3}>
              <Typography mb={1}>Confirm</Typography>
              <Typography>
                You are going to create a new user as below:
              </Typography>
              <TextEditedContent
                content={JSON.stringify(formState, undefined, 4)}
              />
              <Grid
                spacing={3}
                container
                justifyContent="space-between"
                alignItems="center">
                <Grid>
                  <Button
                    variant="contained"
                    color="primary"
                    style={{
                      color: 'white',
                    }}
                    onClick={sendToServer}>
                    Confirm
                  </Button>
                </Grid>
                <Grid>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => {
                      setIncompleteWarningModalOpen(false);
                      setConfirmModalOpen(false);
                    }}>
                    Cancel
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Card>
        </AdminModal>
      </Box>
    </Fragment>
  );
};
IWGDashboardContractsNew.propTypes = propTypes;

export default IWGDashboardContractsNew;
